import { TWELVE_HOUR_TIME_PARSE_FORMAT } from 'features/store/constants';
import {
  getNextOpeningHoursForServiceMode,
  getStoreOperatingHoursForServiceMode,
  isOpenIfNotWithinMinutesOfCloseTodayAndNot24hours,
  readableCloseHourToday,
  readableHour,
} from 'features/store/utils';
import { useIntl } from 'react-intl';

import { IRestaurant } from '@rbi-ctg/store';
import { IOperatingHours, ServiceMode } from 'generated/graphql-gateway';
import { useConfigValue } from 'hooks/configs/use-config-value';
import { useServiceModeStatus } from 'hooks/use-service-mode-status';
import { isPickup } from 'utils/service-mode';

const isWithinOneHourOfClose = (
  hours: IOperatingHours,
  storeTimeZone: string | null | undefined
) => {
  return !isOpenIfNotWithinMinutesOfCloseTodayAndNot24hours(
    hours,
    new Date(Date.now()),
    storeTimeZone || undefined,
    60
  );
};

interface IOpenClosedText {
  isOpen: boolean;
  isWithinOneHourOfClose: boolean;
  text: string;
}

export const useOpenClosedText = (store: IRestaurant): IOpenClosedText => {
  const timeFormat = useConfigValue({
    key: 'timeFormat',
    defaultValue: TWELVE_HOUR_TIME_PARSE_FORMAT,
  });

  const {
    serviceModeStatus: serviceModeStatuses,
    availablePickupServiceModes,
  } = useServiceModeStatus(store);
  const { formatMessage } = useIntl();

  // available === open and not disabled
  const firstAvailablePickUpServiceMode = availablePickupServiceModes[0];

  if (!firstAvailablePickUpServiceMode) {
    // store closed scenario
    let closedText = formatMessage({ id: 'closed' });
    let firstEnabledServiceMode: ServiceMode | null = null;

    for (const [serviceMode, status] of Object.entries(serviceModeStatuses)) {
      if (!isPickup(ServiceMode[serviceMode])) {
        continue;
      }

      // status.available takes into account whether or not the service mode is currently open, so we can't use it here.
      // we just need the first service mode that is enabled so that we can get its opening hours for tomorrow.
      if (status.capable && !status.disabled) {
        firstEnabledServiceMode = ServiceMode[serviceMode];
        break;
      }
    }

    if (firstEnabledServiceMode) {
      // TODO: This does not account for overrides. Replace this when the BE implements a new property
      // that contains the next available opening hours, accounting for overrides.
      const firstEnabledServiceModeOpeningHours = getNextOpeningHoursForServiceMode(
        store,
        firstEnabledServiceMode
      );

      if (firstEnabledServiceModeOpeningHours) {
        const readableOpeningHours = readableHour({
          timeString: firstEnabledServiceModeOpeningHours,
          timeFormat,
          storeTimeZone: store.timezone || undefined,
        });

        const opensAtText = formatMessage(
          { id: 'opensAt' },
          {
            time: readableOpeningHours,
          }
        );

        closedText = `${closedText} - ${opensAtText} `;
      }
    }

    return {
      isOpen: false,
      isWithinOneHourOfClose: false,
      text: closedText,
    };
  }

  const todaysOperatingHours = getStoreOperatingHoursForServiceMode(
    store,
    firstAvailablePickUpServiceMode
  );

  // This is just to satisfy type checks. By this point there are hours of operations for the day, otherwise
  // the service mode wouldn't have `available` set to `true`.
  if (!!todaysOperatingHours && isWithinOneHourOfClose(todaysOperatingHours, store.timezone)) {
    return {
      isOpen: true,
      isWithinOneHourOfClose: true,
      text: formatMessage(
        { id: 'closesAt' },
        { time: readableCloseHourToday(todaysOperatingHours, timeFormat, store.timezone) }
      ),
    };
  }

  return { isOpen: true, isWithinOneHourOfClose: false, text: formatMessage({ id: 'open' }) };
};
